import { Injectable } from '@angular/core';
import { DefaultUrlSerializer, UrlTree } from '@angular/router';
import { TenantService } from './tenant.service';
import { TenantModuleKey } from '../interfaces/tenant.config.interface';

// Racines de routes correspondant à des modules renommables par tenant.
// Les CLÉS de routes restent canoniques partout dans le code (routerLink,
// guards, redirects) — seule l'URL AFFICHÉE change.
const RACINES_MODULES: TenantModuleKey[] = ['DOnet', 'GPAnet', 'MULTInet', 'ULTRAnet'];

// Deuxième segment : ressources dont le NOM MÉTIER change par tenant
// (sinistre→chantier, réclamation→réserve). La clé est le segment canonique
// des routes, la valeur le terme à passer à TenantService.term() — l'alias est
// donc dérivé du dictionnaire de termes, jamais écrit en dur ici.
// Les segments composés (`reclamations-bulk`) ne matchent pas : la
// correspondance porte sur le segment ENTIER.
const RESSOURCES: { [segment: string]: string } = {
  'sinistres': 'sinistres',
  'sinistre': 'sinistre',
  'reclamations': 'réclamations',
  'reclamation': 'réclamation',
};

// Alias d'URL : minuscules et sans diacritique (« réserves » → « reserves »),
// pour ne pas produire d'URL percent-encodée dans la barre d'adresse.
function normaliseSegment(valeur: string): string {
  return valeur.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

/**
 * URLs par tenant (design validé client 2026-07-30) : la barre d'adresse
 * affiche le nom du module du tenant (/1Gnet, /REZnet, /TXnet, /HealthBoard…)
 * tandis que le routeur travaille sur les routes canoniques (/DOnet, /GPAnet).
 *
 * Mécanique : on traduit le PREMIER segment de l'URL dans les deux sens —
 *   parse()     : alias tenant → racine canonique (une URL /REZnet/... collée
 *                 dans le navigateur route vers /GPAnet/...) ;
 *   serialize() : racine canonique → alias tenant (toute navigation affiche
 *                 l'alias). Les anciennes URLs canoniques restent valides.
 *
 * VGE : les labels de modules sont égaux aux clés → tables vides → ce
 * sérialiseur est STRICTEMENT équivalent au DefaultUrlSerializer (no-op).
 * Garde anti-collision : un alias identique à une racine canonique est ignoré.
 */
@Injectable()
export class TenantUrlSerializer extends DefaultUrlSerializer {
  private aliasVersCanonique = new Map<string, string>();
  private canoniqueVersAlias = new Map<string, string>();

  private aliasVersRessource = new Map<string, string>();
  private ressourceVersAlias = new Map<string, string>();

  constructor(tenant: TenantService) {
    super();
    for (const cle of RACINES_MODULES) {
      const alias = tenant.moduleLabel(cle);
      if (!alias || alias === cle || !tenant.moduleEnabled(cle)) { continue; }
      if ((RACINES_MODULES as string[]).indexOf(alias) > -1) { continue; }
      this.aliasVersCanonique.set(alias.toLowerCase(), cle);
      this.canoniqueVersAlias.set(cle.toLowerCase(), alias);
    }

    for (const segment of Object.keys(RESSOURCES)) {
      const alias = normaliseSegment(tenant.term(RESSOURCES[segment]));
      // Terme non traduit (VGE) ou alias entrant en collision avec un segment
      // canonique : on n'aliase pas — l'URL reste celle d'origine.
      if (!alias || alias === segment || RESSOURCES[alias]) { continue; }
      this.aliasVersRessource.set(alias, segment);
      this.ressourceVersAlias.set(segment, alias);
    }
  }

  override parse(url: string): UrlTree {
    return super.parse(this.traduit(url, this.aliasVersCanonique, this.aliasVersRessource));
  }

  override serialize(tree: UrlTree): string {
    return this.traduit(super.serialize(tree), this.canoniqueVersAlias, this.ressourceVersAlias);
  }

  // Racine PUIS ressource. Le second segment n'est traduit que si le premier
  // est une racine de module connue : une URL hors modules (/Admin/sinistre…)
  // n'est jamais réécrite par accident.
  private traduit(url: string, tableRacine: Map<string, string>, tableRessource: Map<string, string>): string {
    const avecRacine = this.remplaceRacine(url, tableRacine);
    if (!tableRessource.size) { return avecRacine; }

    // Appartenance au module évaluée sur l'URL D'ORIGINE : au serialize la
    // racine a déjà été remplacée par l'alias (« 1Gnet »), qui ne figure plus
    // dans RACINES_MODULES — la tester après coup ne matcherait jamais.
    const origine = url.match(/^\/([^\/?#(;]+)([\/?#(;]|$)/);
    if (!origine) { return avecRacine; }
    const racineBrute = decodeURIComponent(origine[1]);
    const estModule = (RACINES_MODULES as string[]).indexOf(racineBrute) > -1
      || this.aliasVersCanonique.has(racineBrute.toLowerCase());
    if (!estModule) { return avecRacine; }

    const m = avecRacine.match(/^\/([^\/?#(;]+)\/([^\/?#(;]+)([\/?#(;]|$)/);
    if (!m) { return avecRacine; }
    const cible = tableRessource.get(decodeURIComponent(m[2]).toLowerCase());
    if (!cible) { return avecRacine; }
    return '/' + m[1] + '/' + cible + avecRacine.slice(2 + m[1].length + m[2].length);
  }

  // Remplace le premier segment du chemin s'il figure dans la table donnée.
  // Préserve query params (?), fragment (#), params matriciels (;) et
  // routes auxiliaires ( ( ).
  private remplaceRacine(url: string, table: Map<string, string>): string {
    if (!table.size || url.charAt(0) !== '/') { return url; }
    const m = url.match(/^\/([^\/?#(;]+)([\/?#(;]|$)/);
    if (!m) { return url; }
    const cible = table.get(decodeURIComponent(m[1]).toLowerCase());
    if (!cible) { return url; }
    return '/' + cible + url.slice(1 + m[1].length);
  }
}
